import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { Chart, ChartModule } from 'angular-highcharts';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { Series, SeriesOptionsType } from "highcharts";

@Component({
  selector: 'test-multi-line-borg-chart',
  providers: [],
  styleUrls: ['./test-multi-line-borg-chart.scss'],
  standalone: true,
  imports: [
    ChartModule
  ],
  templateUrl: './test-multi-line-borg-chart.html'
})
export class TestMultiLineBorgChartComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    public unit = '';

    @Input()
    public title = '';

    @Input()
    public value = 0;

    @Input()
    public data: any[] = [];

    @Output()
    public pointClickEvent = new EventEmitter();


    @Input()
    public subject: Subject<any> = new Subject<string>()

    @Input()
    public showTooltip = true;

    @Input()
    public currentSerieCategory: number | null = null;

    @Input()
    public graphClass: string = 'graphClass';

    public chart: Chart;
    private options: any;
    private subscriptions: Subscription[] = [];

    constructor(
        private translator: TranslateService
    ) {
      this.chart = this.InitChartWithOptions();
    }

    public ngOnInit() {
        if (this.subject) {
            this.subscriptions.push(
                this.subject.subscribe((event) => {
                    if (event === 'delete') {
                        this.removePlotLines();
                    }

                    if (event === 'reflow') {
                        // this.chart.ref.reflow();
                    }
                })
            );
        }
    }

    public ngOnChanges(changes: any) {
        if (changes['data'] && null !== this.data) {
            this.addSeries(this.data);
            this.addPlotLine(this.currentSerieCategory);
        }
    }

    public InitChartWithOptions() {
        const onPointClickedEvent = (point: any) => {
          this.pointClickEvent.emit(point);
        };

        const yAxisBorg: any = {
            0: this.translator.instant('Borg0'),
            1: this.translator.instant('Borg1'),
            2: this.translator.instant('Borg2'),
            3: this.translator.instant('Borg3'),
            4: this.translator.instant('Borg4'),
            5: this.translator.instant('Borg5'),
            6: this.translator.instant('Borg6'),
            7: this.translator.instant('Borg7'),
            8: this.translator.instant('Borg8'),
            9: this.translator.instant('Borg9'),
            10: this.translator.instant('Borg10')
        };

        const self = this;
        const trans = function(name: any) {
            return self.translator.instant(name);
        };

        this.options = {
          chart: {
            type: 'spline',
            events: {
              click: function (this: Highcharts.Chart, event: Highcharts.ChartClickEventObject) {
                // Correct `this` context and use a proper type for the event
                const serie = this.series[0];
                if (serie) {
                  const point = serie.searchPoint(event, true); // Casting to 'any' if necessary
                  if (point) {
                    onPointClickedEvent(point);
                  }
                }
              }
            }
          },
          title: {
            text: this.title,
          },
          xAxis: {
            type: 'datetime',
            dateTimeLabelFormats: {
              month: '%b. %y',
              year: '%Y'
            }
          },
          yAxis: {
            title: {
              text: ''
            },
            min: 0,
            max: 10,
            tickInterval: 1,
            labels: {
              formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
                const value = yAxisBorg[this.value];
                return value !== 'undefined' ? value : this.value;
              }
            }
          },
          tooltip: {
            crosshairs: this.showTooltip,
            enabled: this.showTooltip,
            shared: this.showTooltip,
            xDateFormat: '%A, %d %B %Y',
            useHTML: true,
            pointFormatter: function (this: Highcharts.TooltipFormatterContextObject) {
              return '<strong>' + trans(this.series.name) + ' : </strong>'
                + this.y + '<br />'
                ;
            }
          },
          plotOptions: {
            spline: {
              marker: {
                enabled: true
              }
            },
            series: {
              point: {
                events: {
                  click: function (this: Highcharts.Point, event: Highcharts.PointClickEventObject) {
                    if (self.showTooltip) {
                      self.addPlotLine(this.category);
                      onPointClickedEvent(event.point);
                    }
                  }
                }
              },
              states: {
                hover: {
                  enabled: false
                }
              }
            }
          },
          legend: {
            labelFormatter: function (this: Highcharts.Series) {
              return trans(this.name);
            }
          },
          colors: [
            '#C29D4D',
            '#000000',
            '#6DB58B',
            '#3CB7C7',
            '#99DEFF',
            '#4062D5',
            '#9142DB',
          ],

          // Define the data points. All series have a dummy year
          // of 1970/71 in order to be compared on the same x axis. Note
          // that in JavaScript, months start at 0 for January, 1 for February etc.
          series: null,
          credits: {
            enabled: false
          },
          lang: {
            noData: this.translator.instant('NoData'),
          }
        };

        if (this.showTooltip) {
          this.options.xAxis.crosshair = {
            width: 5,
            color: '#ccc',
            zIndex: 1
          };
        }

        return new Chart(this.options);
    }


    public ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    private addSeries(series: any) {
        let i = 0;
        for (const key of Object.keys(series)) {
            this.chart.removeSeries(i++);
            this.chart.addSeries({
                name: series[key]['name'],
                data: series[key]['data']
            } as SeriesOptionsType, true, true);
        }
    }

    private addPlotLine(category: any) {
        this.subscriptions.push(
            this.chart.ref$.subscribe((ref: any) => {
                ref.xAxis[0].removePlotLine('selected-day');
                ref.xAxis[0].addPlotLine({
                    id: 'selected-day',
                    width: 5,
                    color: '#ccc',
                    dashStyle: 'solid',
                    zIndex: 1,
                    value: category,
                }
                );
            })
        );
    }

    private removePlotLines() {
        this.subscriptions.push(
            this.chart.ref$.subscribe((ref: any) => {
                ref.xAxis[0].removePlotLine('selected-day');
            })
        );
    }

}
